/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useSelector } from 'react-redux';
import { selectCurrentFarmerAccount } from '../features/azure-b2c/storage';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fullAddress, searchAddress } from '../services/als';
import useDebounce from './useDebounce';
import { getUniqueAddresses } from './helpers';

interface UseAccountAddressParam {
  searchText?: string;
}

const defaultParams = {
  searchText: undefined,
};

const useAccountAddress = (params?: UseAccountAddressParam) => {
  const account = useSelector(selectCurrentFarmerAccount);
  const { searchText } = params || defaultParams;
  const debounceSearchText = useDebounce(searchText, 1000);

  const matchingKnownAddresses = getUniqueAddresses(account?.addresses || []).filter((address) => {
    const addressSearchTextFound = !searchText || address.fullAddress.includes(searchText);
    const addressHasAttribute = address.name || address.street;
    return addressHasAttribute && addressSearchTextFound;
  });

  const searchAddressQuery = useQuery({
    queryKey: ['searchAddress', debounceSearchText],
    queryFn: async () => {
      return await searchAddress(debounceSearchText ?? '');
    },
  });

  const getFullAddressMutation = useMutation({
    mutationFn: async (addressId: number) => {
      const fullAddressResult = await fullAddress(addressId);
      // it return as array :(
      return fullAddressResult[0];
    },
  });

  return {
    matchingKnownAddresses,
    searchAddressQuery,
    getFullAddressMutation,
  };
};

export default useAccountAddress;
